import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, Building2, Sparkles, ArrowRight } from 'lucide-react';
import SubpageShell from './SubpageShell';
import SEO from '../components/SEO';
import RainbowText from '../components/RainbowText';
import '../styles/RainbowText.css';

const Impact = () => {
  const navigate = useNavigate();

  return (
    <SubpageShell currentPath="/impact">
      <SEO
        title="Impact"
        description="How N3URO WORLD® measures social value, family outcomes and long-term infrastructure impact across neuro-inclusive environments."
        path="/impact"
      />
      {/* Pinned hero banner FIRST — full-bleed, heading below. Reuses the
          .about-hero-* pattern so behaviour is identical across pages. */}
      <section className="about-hero-block" data-testid="impact-hero">
        <figure className="about-hero-banner" data-testid="impact-hero-banner">
          <img width="1264" height="843" decoding="async" fetchPriority="high"
            src="/img/impact-hero.webp"
            alt="Impact That Lasts — Families. Communities. Futures. Stronger Families, Empowered Children, Inclusive Communities, Real-World Change, A Lasting Legacy."
            loading="eager"
          />
        </figure>
        <div className="about-hero-heading">
          <span className="subpage-eyebrow">Our Impact</span>
          <h1 className="subpage-title about-title-small" data-testid="impact-title">
            <RainbowText>Real change through real partnerships.</RainbowText>
          </h1>
          <div className="subpage-rule" aria-hidden="true" />
        </div>
      </section>

      <main className="subpage-main">
        <section className="subpage-section">
          <p className="subpage-lede" style={{ maxWidth: '68ch' }}>
            N3URO WORLD® measures impact through systemic change &mdash;
            influencing how organisations plan, design and operate
            environments around neurological difference. Our work helps embed
            neuro-inclusive family access into the built environment, creating
            infrastructure that can extend across sectors, places and
            communities.
          </p>
        </section>

        <section className="subpage-section">
          <h2><RainbowText>How we measure impact</RainbowText></h2>
          <div className="subpage-grid">
            <div className="subpage-card">
              <Users className="subpage-card-icon" strokeWidth={1.5} />
              <h4>Family-level outcomes</h4>
              <p>Confidence progression, repeat attendance stability, parent participation continuity.</p>
            </div>
            <div className="subpage-card">
              <Building2 className="subpage-card-icon" strokeWidth={1.5} />
              <h4>Community-level outcomes</h4>
              <p>Daytime family footfall, regeneration lift, civic positioning of partner schemes.</p>
            </div>
            <div className="subpage-card">
              <TrendingUp className="subpage-card-icon" strokeWidth={1.5} />
              <h4>Systems-level outcomes</h4>
              <p>Alignment with SEND, health and early-years pathways across partner regions.</p>
            </div>
            <div className="subpage-card">
              <Sparkles className="subpage-card-icon" strokeWidth={1.5} />
              <h4>Sector-level outcomes</h4>
              <p>Setting a standard for what neuro-inclusive family infrastructure looks like.</p>
            </div>
          </div>
        </section>

        <section className="subpage-section">
          <h2><RainbowText>Our ambition</RainbowText></h2>
          <p>
            N3URO WORLD® is building towards a future where neuro-inclusive
            family access is considered within the infrastructure people use
            every day.
          </p>
          <p>
            Across transport, housing, development, public infrastructure,
            healthcare, hospitality, commercial destinations and community
            environments, our ambition is to influence how places are planned,
            designed and experienced by families.
          </p>
          <p>
            Not one type of building.<br />
            Not one sector.<br />
            A wider standard for family access.
          </p>
        </section>

        <section className="subpage-section">
          <h2><RainbowText>Build access differently</RainbowText></h2>
          <p>
            The environments families use are constantly being built, renewed,
            operated and reimagined.
          </p>
          <p>
            N3URO WORLD® works with the organisations behind those environments
            to bring neuro-inclusive family access into the conversation.
          </p>
          <p>
            Whether you are developing a new environment, reviewing an existing
            one or shaping wider infrastructure strategy, there is a place for
            neurological access within that work.
          </p>
          <div className="subpage-cta-row">
            <button
              type="button"
              onClick={() => navigate('/advisory')}
              className="subpage-btn-primary"
              data-testid="impact-advisory-cta"
            >
              Explore N3URO Advisory
              <ArrowRight size={16} strokeWidth={2.4} aria-hidden="true" />
            </button>
          </div>
        </section>
      </main>
    </SubpageShell>
  );
};

export default Impact;
